import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    Typography, FormGroup
} from '@mui/material';
import TwAutocomplete, { TwAutocompleteOption } from '../../../../components/TwAutocomplete/TwAutocomplete';
import TwCheckbox from '../../../../components/TwCheckboxes/TwCheckboxes';
import {
  useGetTenantCommoditySettingsQuery,
  useUpsertTenantCommoditySettingsMutation
} from '../../../../redux/api/tenant/tenantApi';
import useAuth from '../../../../providers/Auth/Auth.provider';
import { CommodityConfig, MeasurementSystem } from '../../../../providers/User/User.model';
import TwBackdrop from '../../../../components/TwBackdrop/TwBackdrop';

const DEFAULT_COMMODITY_CONFIG = {
  measurement_system: 'IMPERIAL' as MeasurementSystem,
  unit: false,
  dimensions: false,
  commodity_id: false,
  permit_requirement: false,
  make: false,
  model: false,
};

const CommoditySettings = (): JSX.Element => {
  const { data: { userData } } = useAuth();
  const tenantId = userData?.tenant?.id;
  const [measurementSystem, setMeasurementSystem] = useState<MeasurementSystem>(MeasurementSystem.IMPERIAL);
  const [fields, setFields] = useState<CommodityConfig>(DEFAULT_COMMODITY_CONFIG);

  const { data: commoditySettings, isLoading: isCommoditySettingsLoading } = useGetTenantCommoditySettingsQuery(null, {
    skip: !tenantId,
  });
  const [upsertCommoditySettings] = useUpsertTenantCommoditySettingsMutation();

  useEffect(() => {
    if (!commoditySettings && !isCommoditySettingsLoading) {
      setFields(DEFAULT_COMMODITY_CONFIG);
      upsertCommoditySettings({
        config: DEFAULT_COMMODITY_CONFIG,
      });
    } else if (commoditySettings) {
      setMeasurementSystem(commoditySettings.config.measurement_system);
      setFields(commoditySettings.config);
    }
  }, [commoditySettings]);

  const updateCommoditySettings = ({
    measurementSystem,
    fields,
  }: {
    measurementSystem: MeasurementSystem;
    fields: CommodityConfig;
  }): void => {
    upsertCommoditySettings({
      config: {
        ...fields,
        measurement_system: measurementSystem,
      },
    });
  }

  
  return (
    <Box className="app-settings__container">
      <Grid container spacing={1}>
        <Grid item xs={12} sm={3}>
          <Typography variant="h5" className="tab__title">
            Commodities
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage your commodities settings
          </Typography>
        </Grid>

        <Grid item xs={12} sm={9}>
          <Box>
            <Typography variant="h6" className="tab__title">
              Measurement system
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Select the measurement system that will be used for your commodities
            </Typography>

            <TwAutocomplete
              sx={{
                width: {
                  xs: '100%',
                  sm: '300px'
                }
              }}
              name="measurement_system"
              label="Measurement system"
              optionList={[
                { id: 'IMPERIAL', label: 'Imperial', value: 'IMPERIAL' },
                { id: 'METRIC', label: 'Metric', value: 'METRIC' }
              ]}
              value={measurementSystem === 'IMPERIAL' ? 'Imperial' : 'Metric'}
              onChange={(_, value: TwAutocompleteOption | null): void =>
                updateCommoditySettings({
                  measurementSystem:
                    typeof value?.value === 'string'
                      ? (value.value as MeasurementSystem)
                      : MeasurementSystem.IMPERIAL,
                  fields
                })
              }
            />

            <Box component="hr" mt={1} sx={{ borderColor: 'divider' }} />
            <Box mt={2}>
              <Typography variant="h5" className="tab__title">
                Commodities fields
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Enable the fields you will be able to fill in for your commodities
              </Typography>
              <FormGroup>
                <Box display="flex" flexDirection="column">
                  <TwCheckbox
                    label="Unit type"
                    name="unitType"
                    value={fields.unit}
                    checked={fields.unit}
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      updateCommoditySettings({
                        measurementSystem,
                        fields: {
                          ...fields,
                          unit: e.target.checked
                        }
                      })
                    }
                  />

                  <TwCheckbox
                    label="Dimensions (L x W x H)"
                    name="dimensions"
                    value={fields.dimensions}
                    checked={fields.dimensions}
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      updateCommoditySettings({
                        measurementSystem,
                        fields: {
                          ...fields,
                          dimensions: e.target.checked
                        }
                      })
                    }
                  />

                  <TwCheckbox
                    label="Commodity ID"
                    name="commodity_id"
                    value={fields.commodity_id}
                    checked={fields.commodity_id}
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      updateCommoditySettings({
                        measurementSystem,
                        fields: {
                          ...fields,
                          commodity_id: e.target.checked
                        }
                      })
                    }
                  />

                  <TwCheckbox
                    label="Permit requirement"
                    name="permit_requirement"
                    value={fields.permit_requirement}
                    checked={fields.permit_requirement}
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      updateCommoditySettings({
                        measurementSystem,
                        fields: {
                          ...fields,
                          permit_requirement: e.target.checked
                        }
                      })
                    }
                  />

                  <TwCheckbox
                    label="Make"
                    name="make"
                    value={fields.make}
                    checked={fields.make}
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      updateCommoditySettings({
                        measurementSystem,
                        fields: {
                          ...fields,
                          make: e.target.checked
                        }
                      })
                    }
                  />

                  <TwCheckbox
                    label="Model"
                    name="model"
                    value={fields.model}
                    checked={fields.model}
                    onInputChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                      updateCommoditySettings({
                        measurementSystem,
                        fields: {
                          ...fields,
                          model: e.target.checked
                        }
                      })
                    }
                  />
                </Box>
              </FormGroup>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* {isUpsertingCommoditySettings && <TwBackdrop isOpen={isUpsertingCommoditySettings} />} */}
    </Box>
  );
};

export default CommoditySettings;
